import {
  getPollingID,
  subscribeToPriceChange,
  type StripePollingOptions,
} from '../../core/store/checkoutFetch';
import { asPaymentErrorSource, type PaymentErrorSource } from './errors';

export type StripePaymentBackendContext = {
  plan: string;
  token: string;
  analyticsParams: Record<string, unknown>;
  eventsData: object;
  coupon?: string;
};

export type StripePaymentBackendCompletionResult =
  | {
      kind: 'completed';
      subscriptionId: string;
      eventId: string;
      customerId?: string | number;
    }
  | {
      kind: 'failed';
      message: string;
      errorData?: PaymentErrorSource;
      paymentData?: PaymentErrorSource;
    };

type CompleteStripePaymentOnBackendParams = {
  paymentIntentId: string;
  context: StripePaymentBackendContext;
  pollingOptions: StripePollingOptions;
  couldNotCompleteMessage?: string;
  paymentFailedMessage?: string;
};

export const completeStripePaymentOnBackend = async ({
  paymentIntentId,
  context,
  pollingOptions,
  couldNotCompleteMessage = 'Could not complete payment',
  paymentFailedMessage = 'Payment failed',
}: CompleteStripePaymentOnBackendParams): Promise<StripePaymentBackendCompletionResult> => {
  let requestError: { message: string; errorData?: Record<string, unknown> } | undefined;

  const response = await getPollingID(
    '',
    context.plan,
    context.token,
    context.analyticsParams as Parameters<typeof getPollingID>[3],
    context.eventsData,
    (message, errorData) => {
      requestError = { message, errorData };
    },
    paymentIntentId,
    context.coupon ?? '',
    pollingOptions,
  );

  if (requestError) {
    return {
      kind: 'failed',
      message: requestError.message || couldNotCompleteMessage,
      errorData: requestError.errorData,
    };
  }

  if (!response || typeof response !== 'object') {
    return { kind: 'failed', message: couldNotCompleteMessage };
  }

  const result = response as Record<string, unknown>;
  const pollingId = result.polling_result_id;
  if (typeof pollingId !== 'string' || !pollingId) {
    return { kind: 'failed', message: couldNotCompleteMessage };
  }

  const pollingResult = await subscribeToPriceChange(pollingId);
  if (pollingResult?.status && pollingResult.data?.subscription_id) {
    return {
      kind: 'completed',
      subscriptionId: pollingResult.data.subscription_id,
      eventId: pollingResult.data.event_id || '',
      customerId: pollingResult.data.customer_id,
    };
  }

  return {
    kind: 'failed',
    message: pollingResult?.errors?.messages || paymentFailedMessage,
    errorData: asPaymentErrorSource(pollingResult?.errors),
    paymentData: asPaymentErrorSource(pollingResult?.data),
  };
};
